import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Filter, X } from "lucide-react";
import { useRoles } from "@/features/roles/hooks/useRolesQueries";
import { getRoleLabel } from "@/features/roles/constants/role-labels";
import { useUser } from "@/features/auth/store";
import { filterRolesVisibleInUserList } from "@/lib/role-management-policy";

interface UsersFiltersProps {
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  role?: string;
  departmentId?: string;
  isActive?: boolean;
  onRoleChange: (role: string | undefined) => void;
  onDepartmentChange: (departmentId: string | undefined) => void;
  onIsActiveChange: (isActive: boolean | undefined) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  showDepartmentFilter?: boolean;
  departments?: Array<{ id: string; nameAr: string }>;
}

export function UsersFilters({
  searchInput,
  onSearchInputChange,
  role,
  departmentId,
  isActive,
  onRoleChange,
  onDepartmentChange,
  onIsActiveChange,
  onClearFilters,
  hasActiveFilters,
  showDepartmentFilter,
  departments = [],
}: UsersFiltersProps) {
  const sessionUser = useUser();
  const { data: roles = [] } = useRoles();
  const visibleRoles = filterRolesVisibleInUserList(sessionUser, roles);

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="size-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">فلتر المستخدمين</h3>
        </div>
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            className="border-destructive/20 text-destructive hover:bg-destructive/5"
          >
            <X className="mr-1 size-4" />
            مسح الفلاتر
          </Button>
        )}
      </div>

      <div className={`grid grid-cols-1 ${showDepartmentFilter ? "md:grid-cols-4" : "md:grid-cols-3"} gap-4`}>
        <div className="relative">
          <Search className="absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="ابحث بالاسم أو اسم المستخدم أو البريد..."
            value={searchInput}
            onChange={(e) => onSearchInputChange(e.target.value)}
            className="pr-10"
          />
        </div>

        <Select
          value={role ?? ""}
          onValueChange={(v) =>
            onRoleChange(!v || v === "ALL" ? undefined : v)
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="اختر الدور" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">كل الأدوار</SelectItem>
            {visibleRoles.map((r) => (
              <SelectItem key={r.code} value={r.role}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {showDepartmentFilter && (
          <Select
            value={departmentId ?? ""}
            onValueChange={(v) =>
              onDepartmentChange(!v || v === "ALL" ? undefined : v)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="اختر الدائرة" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">كل الدوائر</SelectItem>
              {departments.map((dept) => (
                <SelectItem key={dept.id} value={dept.id}>
                  {dept.nameAr}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select
          value={isActive === undefined ? "" : String(isActive)}
          onValueChange={(v) =>
            onIsActiveChange(!v || v === "ALL" ? undefined : v === "true")
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="اختر الحالة" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">كل الحالات</SelectItem>
            <SelectItem value="true">نشط</SelectItem>
            <SelectItem value="false">معطل</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <span className="text-sm font-medium text-muted-foreground">الفلاتر النشطة:</span>
          {searchInput && (
            <Badge variant="secondary">البحث: {searchInput}</Badge>
          )}
          {role && (
            <Badge variant="secondary">
              الدور: {getRoleLabel(role, Object.fromEntries(roles.map((r) => [r.role, r.name])))}
            </Badge>
          )}
          {departmentId && (
            <Badge variant="secondary">
              الدائرة:{" "}
              {departments.find((d) => d.id === departmentId)?.nameAr ?? departmentId}
            </Badge>
          )}
          {isActive !== undefined && (
            <Badge variant="secondary">
              الحالة: {isActive ? "نشط" : "معطل"}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
